import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PORTAL } from '../portalAuth';
import { STORE_BASE } from '../routeBase';

// Lands here after Razorpay redirects the browser back from a Payment Link
// (UPI or Standard) — the customer may be on a completely different device
// or a fresh, unauthenticated tab (e.g. a UPI app's in-app browser), so this
// page never assumes a logged-in portal session. Ownership + authenticity
// are established purely by the order_id + Razorpay's own signature, which
// POST /order/:id/razorpay/verify-link checks server-side.
export default function PaymentResult() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState('checking'); // 'checking' | 'success' | 'failed' | 'unknown'
  const [message, setMessage] = useState('');
  const [orderNumber, setOrderNumber] = useState('');

  useEffect(() => {
    const orderId = params.get('order_id');
    const paymentId = params.get('razorpay_payment_id');
    const linkId = params.get('razorpay_payment_link_id');
    const linkRef = params.get('razorpay_payment_link_reference_id');
    const linkStatus = params.get('razorpay_payment_link_status');
    const signature = params.get('razorpay_signature');

    if (!orderId || !paymentId || !linkId || !signature) { setStatus('unknown'); return; }

    PORTAL.post(`/order/${orderId}/razorpay/verify-link`, {
      razorpay_payment_id: paymentId, razorpay_payment_link_id: linkId,
      razorpay_payment_link_reference_id: linkRef, razorpay_payment_link_status: linkStatus,
      razorpay_signature: signature,
    }).then(r => {
      setStatus('success');
      setOrderNumber(r.data.order_number || '');
    }).catch(e => {
      setStatus('failed');
      setMessage(e.response?.data?.error || 'We could not confirm this payment.');
      setOrderNumber(e.response?.data?.order_number || '');
    });
  }, [params]);

  return (
    <div style={{ maxWidth: 560, margin: '60px auto', padding: 20 }}>
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 36, textAlign: 'center' }}>
        {status === 'checking' && (
          <>
            <div style={{ width: 44, height: 44, margin: '0 auto', border: '3px solid #eee', borderTopColor: 'var(--sf-accent)', borderRadius: '50%', animation: 'sf-spin 0.8s linear infinite' }} />
            <h1 style={{ fontSize: 22, marginTop: 18 }}>Confirming your payment…</h1>
            <p style={{ color: '#888', fontSize: 13.5, marginTop: 6 }}>Please don't close this page.</p>
            <style>{'@keyframes sf-spin { to { transform: rotate(360deg); } }'}</style>
          </>
        )}
        {status === 'success' && (
          <>
            <i className="ti ti-circle-check" style={{ fontSize: 56, color: '#16a34a' }} />
            <h1 style={{ fontSize: 24, marginTop: 12 }}>Payment received successfully</h1>
            <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>
              Thank you — your payment for order <strong>{orderNumber}</strong> has been confirmed. A receipt has been recorded against your account.
            </p>
            <Link to={`${STORE_BASE}/orders`} className="sf-btn-outline" style={btn()}>View my orders</Link>
            <Link to={`${STORE_BASE}/shop`} className="sf-btn-outline" style={{ ...btn(), marginLeft: 10 }}>Continue shopping</Link>
          </>
        )}
        {status === 'failed' && (
          <>
            <i className="ti ti-circle-x" style={{ fontSize: 56, color: '#dc2626' }} />
            <h1 style={{ fontSize: 24, marginTop: 12 }}>Payment not completed</h1>
            <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>{message} {orderNumber ? <>Your order <strong>{orderNumber}</strong> is still on file — you can try paying again from My Orders, or contact us for help.</> : null}</p>
            <Link to={`${STORE_BASE}/orders`} className="sf-btn-outline" style={btn()}>Go to my orders</Link>
            <Link to={`${STORE_BASE}/contact`} className="sf-btn-outline" style={{ ...btn(), marginLeft: 10 }}>Contact us</Link>
          </>
        )}
        {status === 'unknown' && (
          <>
            <i className="ti ti-help-circle" style={{ fontSize: 56, color: '#9a6a00' }} />
            <h1 style={{ fontSize: 24, marginTop: 12 }}>Payment status unavailable</h1>
            <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>We couldn't read the payment details from this link. If you completed a payment, please check My Orders for the latest status.</p>
            <Link to={`${STORE_BASE}/orders`} className="sf-btn-outline" style={btn()}>Go to my orders</Link>
          </>
        )}
      </div>
    </div>
  );
}

const btn = () => ({ display: 'inline-block', marginTop: 18, padding: '10px 24px', borderRadius: 22, border: '1.5px solid var(--sf-accent)', textDecoration: 'none', fontSize: 14, fontWeight: 600 });
